import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import * as api from "../api";
import "../App.css";
import { useEEGAnalysis } from "../hooks/useEEGAnalysis";
import HeatmapVisualization from "./HeatmapVisualization";
import Button from "./ui/Button";
import Select from "./ui/Select";

import {
  fundacjaOrchideaDisplayConfig,
  machineTypeOptions,
} from "./eegAnalysisHelpers";

const EEGAnalysis = ({ 
  selectedSessionForAnalysis, 
  refreshPatientDetails,
  isLeftSidebarHidden = false,
  setIsLeftSidebarHidden = () => {},
}) => {
  const [displayMetric, setDisplayedMetric] = useState("amplitude"); // 'amplitude' or 'percentage'

  const {
    eegFile,
    isLoadingUpload,
    uploadError,
    analysisDeviations,
    isLoadingAnalysis,
    analysisError,
    heatmapRawData,
    isLoadingHeatmap,
    heatmapError,
    deviationDisplayThreshold,
    machineTypeForAnalysis,
    docxUploaded,
    normativeProfiles,
    selectedNormativeProfileName,
    setSelectedNormativeProfileName,
    handleFileChange,
    handleMachineTypeChange,
    handleStartAnalysis,
    handleFileUpload,
    // setDeviationDisplayThreshold,
    setAnalysisDeviations,
    suggestedInterpretationThemes,
    sessionRatios,
    isLoadingRatios,
    ratioError,
    requireFullCoverage,
    setRequireFullCoverage,
  } = useEEGAnalysis(selectedSessionForAnalysis, refreshPatientDetails, displayMetric);

  // Automatically trigger analysis once the DOCX file is uploaded
  useEffect(() => {
    if (
      docxUploaded &&
      !isLoadingAnalysis &&
      !isLoadingHeatmap &&
      !heatmapRawData
    ) {
      handleStartAnalysis();
    }
  }, [
    docxUploaded,
    isLoadingAnalysis,
    isLoadingHeatmap,
    heatmapRawData,
    handleStartAnalysis,
  ]);

  const handleSaveDeviationCallback = useCallback(
    async (deviationId, updatePayload) => {
      try {
        const response = await api.updateDeviation(deviationId, updatePayload);
        setAnalysisDeviations((prevDevs) =>
          prevDevs.map((d) => (d.id === deviationId ? response.data : d))
        );
        alert("Deviation updated successfully!");
      } catch (err) {
        console.error("Error updating deviation from EEGAnalysis:", err);
        alert(err.response?.data?.detail || "Failed to update deviation.");
        throw err;
      }
    },
    [setAnalysisDeviations]
  );

  const handleUpdateDiagnosticsCenterCallback = useCallback(
    async (deviationId) => {
      try {
        await api.updateDiagnosticsCenterPattern(deviationId);
        console.log(
          "Diagnostics center potentially updated for deviation:",
          deviationId
        );
      } catch (dcError) {
        console.error(
          "Error updating diagnostics center from EEGAnalysis:",
          dcError
        );
        alert(
          dcError.response?.data?.detail ||
            "Failed to update diagnostics center."
        );
        throw dcError;
      }
    },
    []
  );

  const displayedDeviations = React.useMemo(() => {
    try {
      if (
        !analysisDeviations ||
        !Array.isArray(analysisDeviations) ||
        analysisDeviations.length === 0 ||
        !heatmapRawData
      )
        return [];

      const undiagnosed = analysisDeviations.filter((d) => {
        const currentRawDataCell = heatmapRawData.find(
          (cell) =>
            cell.channel === d.channel &&
            cell.frequency_band === d.frequency_band
        );

        return (
          d &&
          typeof d === "object" &&
          d.status === "undiagnosed" &&
          (displayMetric === "amplitude"
            ? currentRawDataCell?.is_amplitude_deviation
            : currentRawDataCell?.is_percentage_deviation)
        );
      });
      const diagnosedAndOther = analysisDeviations.filter((d) => {
        const currentRawDataCell = heatmapRawData.find(
          (cell) =>
            cell.channel === d.channel &&
            cell.frequency_band === d.frequency_band
        );

        return (
          d &&
          typeof d === "object" &&
          d.status !== "undiagnosed" &&
          (displayMetric === "amplitude"
            ? currentRawDataCell?.is_amplitude_deviation
            : currentRawDataCell?.is_percentage_deviation)
        );
      });

      // Sort with safety checks
      undiagnosed.sort((a, b) => {
        const magA = typeof a.magnitude === "number" ? a.magnitude : 0;
        const magB = typeof b.magnitude === "number" ? b.magnitude : 0;
        return magB - magA; // Sort by descending magnitude
      });

      // const thresholdCount = Math.ceil(
      //   undiagnosed.length * (deviationDisplayThreshold / 100)
      // );
      // const topUndiagnosed = undiagnosed.slice(0, thresholdCount);

      // return [...diagnosedAndOther, ...topUndiagnosed].sort((a, b) => {
      return [...diagnosedAndOther, ...undiagnosed].sort((a, b) => {
        // Simple sort for consistency: by channel then band
        // Add safety checks for missing properties
        const channelA = a.channel || "";
        const bandA = a.frequency_band || "";
        const channelB = b.channel || "";
        const bandB = b.frequency_band || "";

        const valA = `${channelA}-${bandA}`;
        const valB = `${channelB}-${bandB}`;
        return valA.localeCompare(valB);
      });
    } catch (err) {
      console.error("Error processing deviations:", err);
      return []; // Return empty on error
    }
  }, [analysisDeviations, displayMetric, heatmapRawData]);

  // Create a quick lookup for displayed deviations (channel-band)
  const displayedDeviationsLookup = React.useMemo(() => {
    try {
      const lookup = new Set(); // Use a Set to store the actual deviation object for value display

      console.log({
        displayedDeviations,
      });

      if (Array.isArray(displayedDeviations) && heatmapRawData) {
        displayedDeviations.forEach((dev) => {
          if (dev && dev.channel && dev.frequency_band) {
            const currentRawDataCell = heatmapRawData.find(
              (cell) =>
                cell.channel === dev.channel &&
                cell.frequency_band === dev.frequency_band
            );

            console.log({
              currentRawDataCell,
            });
            if (!currentRawDataCell) return;

            if (dev.status !== "irrelevant" && dev.status !== "diagnosed") {
              lookup.add(
                `${dev.channel.toUpperCase()}-${dev.frequency_band.toLowerCase()}`
              );
            }
          }
        });
      }
      return lookup;
    } catch (err) {
      console.error("Error creating deviations lookup:", err);
      return new Set(); // Return empty set on error
    }
  }, [displayedDeviations, heatmapRawData]);

  // Use the machine-specific config for display order
  // const heatmapChannelOrder = fundacjaOrchideaDisplayConfig.channels;
  const heatmapBandOrder = fundacjaOrchideaDisplayConfig.bands;

  // Build options for normative profile Select
  const normativeOptions = (normativeProfiles || []).map((p) => ({
    value: p.name,
    label: p.name,
  }));

  // Dynamically determine channels to display based on available data
  // Preserves order: channels appear in the order of first appearance from API
  const channelsForTableRows = React.useMemo(() => {
    if (heatmapRawData && Array.isArray(heatmapRawData) && heatmapRawData.length > 0) {
      // Collect channels in order of first appearance (preserves source file order)
      const seen = new Set();
      const orderedChannels = [];
      
      for (const cellData of heatmapRawData) {
        const channel = cellData.channel;
        if (channel && !seen.has(channel)) {
          seen.add(channel);
          orderedChannels.push(channel);
        }
      }
      
      if (orderedChannels.length > 0) {
        return orderedChannels;
      }
    }
    
    // Fallback: Use static config for legacy compatibility
    console.warn("[EEGAnalysis] Using fallback channel order from static config");
    return fundacjaOrchideaDisplayConfig.channels;
  }, [heatmapRawData]); // Dependency: heatmapRawData

  if (!selectedSessionForAnalysis) {
    return (
      <motion.div
        className="p-2 md:p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 md:h-5 md:w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className="text-gray-500 text-xs">Select a session for analysis</p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8">
      <motion.div
        className="flex justify-between items-center"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col gap-2">
          <h3 className="text-base md:text-lg font-semibold text-[#1c3f70]">
            {selectedSessionForAnalysis.session_date
              ? new Date(selectedSessionForAnalysis.session_date).toLocaleString()
              : new Date(selectedSessionForAnalysis.created_at).toLocaleString()}
          </h3>
          <p className="text-xs md:text-sm text-gray-600">
            Patient: {selectedSessionForAnalysis.patient_name || "N/A"}
            <span className="ml-2 px-2 py-0.5 md:px-2.5 md:py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
              {selectedSessionForAnalysis.session_type}
            </span>
          </p>
        </div>

        <div className="w-1/2 md:w-1/3">
          <Select
            label="Machine Type"
            defaultValue={machineTypeForAnalysis}
            options={machineTypeOptions}
            onChange={handleMachineTypeChange}
            width="w-full"
            className="mt-3"
            key={machineTypeForAnalysis}
          />
        </div>
        <div className="w-1/2 md:w-1/3 ml-2">
          <Select
            label="Normative Profile"
            defaultValue={selectedNormativeProfileName}
            options={normativeOptions}
            onChange={(e) => setSelectedNormativeProfileName(e.target.value)}
            width="w-full"
            className="mt-3"
            key={selectedNormativeProfileName}
          />
        </div>
      </motion.div>

      {/* File Upload Section - hide when a DOCX file is already uploaded */}
      {!docxUploaded && !heatmapRawData && (
        <motion.div
          className="p-3 md:p-6 bg-white rounded-xl border border-gray-100 shadow-md overflow-hidden relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-purple-500"></div>

          <div className="flex items-center mb-3 md:mb-5">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-[#2a5caa] to-[#3963aa] flex items-center justify-center text-white font-bold mr-2 md:mr-3 shadow-md">
              1
            </div>
            <h4 className="font-semibold text-[#1c3f70] text-base md:text-lg">
              Upload EEG DOCX File
            </h4>
          </div>

          <div className="flex flex-col space-y-3 md:space-y-4">
            <label
              className={`flex flex-col items-center justify-center px-4 py-6 md:px-6 md:py-8 bg-gray-50 border-2 border-dashed ${
                eegFile ? "border-blue-300 bg-blue-50" : "border-gray-200"
              } rounded-lg cursor-pointer hover:bg-gray-50 transition-all group relative overflow-hidden`}
            >
              <div className="relative z-10 text-center">
                <svg
                  className={`mx-auto h-8 w-8 md:h-12 md:w-12 ${
                    eegFile ? "text-blue-500" : "text-gray-400"
                  } mb-2 group-hover:text-blue-500 transition-colors`}
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>

                <p
                  className={`text-xs md:text-sm ${
                    eegFile ? "text-blue-600 font-medium" : "text-gray-500"
                  } group-hover:text-blue-600 transition-colors`}
                >
                  {eegFile ? (
                    <span className="bg-blue-100 px-2 py-1 md:px-3 md:py-1 rounded-full truncate max-w-[200px] inline-block">
                      {eegFile.name}
                    </span>
                  ) : (
                    <>
                      <span className="font-semibold">Click to select</span> or
                      drag and drop
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supports .docx EEG report files exported from EEG suites
                </p>
              </div>

              <input
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                disabled={isLoadingUpload}
                className="hidden"
              />

              <div
                className={`absolute ${
                  eegFile ? "opacity-10" : "opacity-0"
                } group-hover:opacity-10 transition-opacity right-0 top-1/2 transform -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-blue-400 rounded-full pointer-events-none`}
              ></div>
            </label>

            <Button
              onClick={handleFileUpload}
              disabled={!eegFile || isLoadingUpload}
              className={`relative px-3 py-2 md:px-5 md:py-2.5 text-white text-xs md:text-sm font-medium rounded-lg shadow-sm overflow-hidden ${
                !eegFile || isLoadingUpload
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-[#2a5caa] to-[#3963aa] hover:from-[#3963aa] hover:to-[#2a5caa]"
              }`}
            >
              {isLoadingUpload ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-3 w-3 md:h-4 md:w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                <>
                  <span className="relative z-10 flex items-center">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                    Upload File & Start Analysis
                  </span>
                  <span className="absolute top-0 left-0 w-full h-full bg-white opacity-10 transform -translate-x-full hover:translate-x-0 transition-transform duration-300 z-0"></span>
                </>
              )}
            </Button>

            <AnimatePresence>
              {uploadError && (
                <motion.div
                  className="p-3 md:p-4 bg-red-50 border-l-4 border-red-500 rounded-lg"
                  initial={{ opacity: 0, height: 0, y: 10 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: 10 }}
                >
                  <div className="flex">
                    <svg
                      className="h-4 w-4 md:h-5 md:w-5 text-red-500 mr-1 md:mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-red-600 text-xs md:text-sm font-medium">
                      {uploadError}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* Analysis Status Section - only shown when DOCX data is available */}
      {docxUploaded && !heatmapRawData && (
        <motion.div
          className="p-3 md:p-6 bg-white rounded-xl border border-gray-100 shadow-md overflow-hidden relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-teal-500"></div>

          <div className="flex items-center mb-3 md:mb-5">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center text-white font-bold mr-2 md:mr-3 shadow-md flex-shrink-0">
              2
            </div>
            <h4 className="font-semibold text-[#1c3f70] text-base md:text-lg">
              Analysis Status
            </h4>
          </div>

          <div className="flex flex-col space-y-3 md:space-y-4">
            {isLoadingAnalysis || isLoadingHeatmap ? (
              <div className="flex items-center justify-center p-4 md:p-8">
                <svg
                  className="animate-spin mr-2 md:mr-3 h-4 w-4 md:h-5 md:w-5 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-blue-700 text-xs md:text-sm font-medium">
                  {isLoadingAnalysis
                    ? "Analyzing qEEG data..."
                    : "Loading heatmap visualization..."}
                </p>
              </div>
            ) : (
              <div className="flex justify-center">
                <Button
                  onClick={handleStartAnalysis}
                  disabled={isLoadingAnalysis || isLoadingHeatmap}
                  className={`relative inline-flex items-center justify-center px-4 py-2 md:px-6 md:py-3 text-white text-xs md:text-sm font-medium rounded-lg shadow-lg ${
                    isLoadingAnalysis || isLoadingHeatmap
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600"
                  } overflow-hidden`}
                >
                  <span className="relative z-10 flex items-center group">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2 group-hover:animate-pulse"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    Start Analysis & Load Heatmap
                  </span>
                  <span className="absolute top-0 left-0 w-full h-full bg-white opacity-10 transform -translate-x-full hover:translate-x-0 transition-transform duration-300 z-0"></span>
                </Button>
              </div>
            )}

            <AnimatePresence>
              {(analysisError || heatmapError) && (
                <motion.div
                  className="p-3 md:p-4 bg-red-50 border-l-4 border-red-500 rounded-lg"
                  initial={{ opacity: 0, height: 0, y: 10 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: 10 }}
                >
                  <div className="flex">
                    <svg
                      className="h-4 w-4 md:h-5 md:w-5 text-red-500 mr-1.5 md:mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      {analysisError && (
                        <p className="text-red-600 text-xs md:text-sm font-medium">
                          Analysis Error: {analysisError}
                        </p>
                      )}
                      {heatmapError && (
                        <p className="text-red-600 text-xs md:text-sm font-medium mt-1">
                          Heatmap Error: {heatmapError}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* Heatmap section - only shown when data is available */}
      {heatmapRawData && (
        <HeatmapVisualization
          heatmapRawData={heatmapRawData}
          analysisDeviations={analysisDeviations}
          channelsForTableRows={channelsForTableRows}
          heatmapBandOrder={heatmapBandOrder}
          displayedDeviationsLookup={displayedDeviationsLookup}
          displayMetric={displayMetric}
          setDisplayMetric={setDisplayedMetric}
          percentageDeviationThreshold={deviationDisplayThreshold}
          onSaveDeviation={handleSaveDeviationCallback}
          onUpdateDiagnosticsCenter={handleUpdateDiagnosticsCenterCallback}
          suggestedInterpretationThemes={suggestedInterpretationThemes}
          isLoadingAnalysis={isLoadingAnalysis}
          sessionRatios={sessionRatios}
          isLoadingRatios={isLoadingRatios}
          ratioError={ratioError}
          selectedNormativeProfileName={selectedNormativeProfileName}
          requireFullCoverage={requireFullCoverage}
          setRequireFullCoverage={setRequireFullCoverage}
          isLeftSidebarHidden={isLeftSidebarHidden}
          setIsLeftSidebarHidden={setIsLeftSidebarHidden}
        />
      )}

      {/* Deviations Threshold Control - only shown when deviations exist / removed for the time being */}
      {/* {
        <motion.div
          className="bg-white p-3 md:p-6 rounded-xl border border-gray-100 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center mb-3 md:mb-5">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 flex items-center justify-center text-white font-bold mr-2 md:mr-3 shadow-md flex-shrink-0">
              4
            </div>
            <h4 className="font-semibold text-[#1c3f70] text-base md:text-lg">
              Configure Deviation Display
            </h4>
          </div>

          <div className="flex flex-col md:flex-row md:items-center flex-wrap space-y-2 md:space-y-0 md:space-x-4">
            <label
              htmlFor="deviationThreshold"
              className="text-xs md:text-sm text-gray-700 flex-shrink-0"
            >
              Show Top % of Undiagnosed Deviations:
            </label>
            <div className="flex items-center gap-2 flex-grow">
              <input
                type="range"
                id="deviationThreshold"
                name="deviationThreshold"
                min="0"
                max="100"
                value={deviationDisplayThreshold}
                onChange={(e) =>
                  setDeviationDisplayThreshold(parseInt(e.target.value))
                }
                className="flex-grow"
              />
              <span className="text-xs md:text-sm font-medium text-gray-700 w-8 md:w-10 text-right">
                {deviationDisplayThreshold}%
              </span>
            </div>
          </div>
        </motion.div>
      } */}

      {/* Loading state when analyzing */}
      {isLoadingAnalysis && !heatmapRawData && (
        <motion.div
          className="flex justify-center items-center py-6 md:py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="w-4 h-4 md:w-6 md:h-6 rounded-full bg-[#2a5caa] animate-pulse mr-2"></div>
          <p className="text-gray-600 text-sm md:text-base">
            Analyzing data, please wait...
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default EEGAnalysis;
